import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  IconButton, Menu, MenuItem, ListItemText, Chip, TableSortLabel, Tabs, Tab,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import axios from "../../../config/axiosConfig";
import { useAuth } from "../../../context/AuthContext";
import EmitirVacacionDialog from "../components/EmitirVacacionDialog";
import RegistrarDisponibilidadDialog from "../components/RegistrarDisponibilidadDialog";
import EmitirMemoDialog from "../components/EmitirMemoDialog";
import AdicionarPersonalDialog from "../components/AdicionarPersonalDialog";

const Th = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.78rem",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const ROLES_SUELDOS = ["admin", "gerente", "conta"];
const ACCIONES_HABILITADAS = ["Vacación", "Disponibilidad", "Historial", "Emitir memo"];
const ACCIONES = ["Vacación", "Disponibilidad", "Historial", "Emitir memo"];

// per_empleado/per_contrato son datos legacy con años de antigüedad — una fecha
// vacía se guardaba como '0000-00-00', que el driver devuelve como 30/11/1899.
// Se trata como "sin dato" (año < 1950, ninguna fecha real de RRHH cae ahí).
const formatFecha = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getFullYear() < 1950) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const nombreCompleto = (r) => [r.nombre, r.paterno, r.materno].filter(Boolean).join(" ");

// Valor "ordenable" por cada columna — nombre se arma de 3 campos, y días
// disponibles necesita compararse como número, no como string.
const VALOR_ORDEN = {
  nombre: nombreCompleto,
  cargo: (r) => r.cargo,
  fecha_ini: (r) => r.fecha_ini,
  fecha_afi: (r) => r.fecha_afi,
  fecha_con: (r) => r.fecha_con,
  ci: (r) => r.ci,
  departamento: (r) => r.departamento,
  dias_disponibles: (r) => Number(r.dias_disponibles),
};

const FilaAcciones = ({ row, soyAprobador, onVacacion, onDisponibilidad, onHistorial, onEmitirMemo }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const cerrar = () => setAnchorEl(null);
  const HANDLERS = { Vacación: onVacacion, Disponibilidad: onDisponibilidad, Historial: onHistorial, "Emitir memo": onEmitirMemo };
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={cerrar}>
        {ACCIONES.map((accion) => {
          const habilitado = soyAprobador && ACCIONES_HABILITADAS.includes(accion);
          return (
            <MenuItem
              key={accion}
              disabled={!habilitado}
              onClick={habilitado ? () => { cerrar(); HANDLERS[accion](row); } : undefined}
            >
              <ListItemText
                primary={accion}
                secondary={habilitado ? undefined : "Próximamente"}
                secondaryTypographyProps={{ fontSize: "0.68rem" }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const AdministracionPersonalPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [listaHistorico, setListaHistorico] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(true);
  const [errorHistorico, setErrorHistorico] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "nombre", direction: "asc" });
  const [soyAprobador, setSoyAprobador] = useState(false);
  const [vacacionRow, setVacacionRow] = useState(null);
  const [disponibilidadRow, setDisponibilidadRow] = useState(null);
  const [memoRow, setMemoRow] = useState(null);
  const [adicionarOpen, setAdicionarOpen] = useState(false);
  const [sinPermiso, setSinPermiso] = useState(false);

  const puedeVerSueldos = ROLES_SUELDOS.includes(user?.role);

  const cargarLista = () => {
    setLoading(true);
    setError("");
    axios.get("/api/administracion-personal/activos")
      .then(({ data }) => setLista(data?.data || []))
      .catch((e) => {
        if (e?.response?.status === 403) {
          setSinPermiso(true);
        } else {
          setError(e?.response?.data?.message || "Error al cargar el personal.");
        }
      })
      .finally(() => setLoading(false));
  };

  const cargarListaHistorico = () => {
    setLoadingHistorico(true);
    setErrorHistorico("");
    axios.get("/api/administracion-personal/todos")
      .then(({ data }) => setListaHistorico(data?.data || []))
      .catch((e) => {
        if (e?.response?.status !== 403) {
          setErrorHistorico(e?.response?.data?.message || "Error al cargar el histórico de personal.");
        }
      })
      .finally(() => setLoadingHistorico(false));
  };

  useEffect(() => {
    cargarLista();
    cargarListaHistorico();
    axios.get("/api/permisos/soy-aprobador")
      .then(({ data }) => setSoyAprobador(Boolean(data?.data?.esAprobador)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listaActual = tab === 0 ? lista : listaHistorico;
  const cargandoActual = tab === 0 ? loading : loadingHistorico;
  const errorActual = tab === 0 ? error : errorHistorico;

  const listaFiltrada = useMemo(() => {
    const texto = busqueda.trim().toLowerCase();
    if (!texto) return listaActual;
    return listaActual.filter((r) =>
      [nombreCompleto(r), r.cargo, r.departamento].join(" ").toLowerCase().includes(texto)
    );
  }, [listaActual, busqueda]);

  const listaOrdenada = useMemo(() => {
    const { key, direction } = sortConfig;
    const getValue = VALOR_ORDEN[key];
    const factor = direction === "asc" ? 1 : -1;
    return [...listaFiltrada].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * factor;
      return String(av ?? "").localeCompare(String(bv ?? ""), "es", { numeric: true, sensitivity: "base" }) * factor;
    });
  }, [listaFiltrada, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortableTh = ({ children, sortKey, align }) => (
    <Th align={align}>
      <TableSortLabel
        active={sortConfig.key === sortKey}
        direction={sortConfig.key === sortKey ? sortConfig.direction : "asc"}
        onClick={() => handleSort(sortKey)}
      >
        {children}
      </TableSortLabel>
    </Th>
  );

  if (sinPermiso) {
    return (
      <DashboardLayout>
        <PageHeader title="Administración Personal" showBack={false} />
        <Box sx={{ p: 2 }}>
          <Alert severity="warning">No tienes permiso para ver esta información.</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader
        title="Administración Personal"
        subtitle={tab === 0 ? "Listado de personal activo" : "Listado de todo el personal, activo e inactivo"}
        showBack={false}
      />
      <Box sx={{ p: 1 }}>
        <Paper variant="outlined" sx={{ mb: 1, overflow: "hidden" }}>
          <Tabs
            value={tab}
            onChange={(_, v) => { setTab(v); setSortConfig({ key: "nombre", direction: "asc" }); }}
            sx={{ minHeight: 34, "& .MuiTab-root": { minHeight: 34, py: 0.5, fontSize: 12, fontWeight: 700, textTransform: "none" } }}
          >
            <Tab label="Listado personal" />
            <Tab label="Listado personal histórico" />
          </Tabs>
        </Paper>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1, flexWrap: "wrap" }}>
          <TextField
            size="small"
            label="Buscar por nombre, cargo o departamento"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: "text.secondary" }} /> }}
            sx={{ width: { xs: "100%", sm: 320 } }}
          />
          <Typography variant="caption" color="text.secondary">
            {listaFiltrada.length} de {listaActual.length}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {puedeVerSueldos && (
            <Button size="small" variant="outlined" onClick={() => navigate("/administracion-personal/detalle")}>
              Ver detalle
            </Button>
          )}
          {soyAprobador ? (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setAdicionarOpen(true)}>
              Adicionar personal
            </Button>
          ) : (
            <Tooltip title="No tienes permiso para esta acción">
              <span>
                <Button size="small" variant="contained" startIcon={<AddIcon />} disabled>
                  Adicionar personal
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {errorActual && <Alert severity="error" sx={{ mb: 1 }}>{errorActual}</Alert>}

        {cargandoActual ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : listaFiltrada.length === 0 ? (
          <Alert severity="info">No se encontraron registros.</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small" sx={{ "& td": { py: 0.4, px: 0.75, fontSize: "0.78rem" } }}>
                <TableHead>
                  <TableRow>
                    <SortableTh sortKey="nombre">Nombre</SortableTh>
                    <SortableTh sortKey="cargo">Cargo</SortableTh>
                    <SortableTh sortKey="fecha_ini">Fecha Inicio</SortableTh>
                    <SortableTh sortKey="fecha_afi">Fecha Afiliación</SortableTh>
                    <SortableTh sortKey="fecha_con">Fecha Contrato</SortableTh>
                    <SortableTh sortKey="ci">C.I.</SortableTh>
                    <SortableTh sortKey="departamento">Departamento</SortableTh>
                    <SortableTh sortKey="dias_disponibles" align="center">Días disponibles</SortableTh>
                    {tab === 1 && <Th align="center">Estado</Th>}
                    <Th align="center">Acciones</Th>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {listaOrdenada.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{nombreCompleto(r)}</TableCell>
                      <TableCell>{r.cargo}</TableCell>
                      <TableCell>{formatFecha(r.fecha_ini)}</TableCell>
                      <TableCell>{formatFecha(r.fecha_afi)}</TableCell>
                      <TableCell>{formatFecha(r.fecha_con)}</TableCell>
                      <TableCell>{[r.ci, r.procedencia_ci].filter(Boolean).join(" ")}</TableCell>
                      <TableCell>{r.departamento}</TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={r.dias_disponibles}
                          color={Number(r.dias_disponibles) < 0 ? "error" : "default"}
                          sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                        />
                      </TableCell>
                      {tab === 1 && (
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={r.estado_contrato === "Vigente" ? "Activo" : "Inactivo"}
                            color={r.estado_contrato === "Vigente" ? "success" : "default"}
                            variant={r.estado_contrato === "Vigente" ? "filled" : "outlined"}
                            sx={{ height: 19, fontSize: "0.68rem", "& .MuiChip-label": { px: 0.75 } }}
                          />
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <FilaAcciones
                          row={r}
                          soyAprobador={soyAprobador}
                          onVacacion={setVacacionRow}
                          onDisponibilidad={setDisponibilidadRow}
                          onHistorial={(row) => navigate(`/administracion-personal/empleado/${row.id}`)}
                          onEmitirMemo={setMemoRow}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>

      <EmitirVacacionDialog
        open={Boolean(vacacionRow)}
        empleado={vacacionRow ? { id: vacacionRow.id, nombre: nombreCompleto(vacacionRow), cargo: vacacionRow.cargo } : null}
        onClose={() => {
          setVacacionRow(null);
          cargarLista();
          cargarListaHistorico();
        }}
      />

      <RegistrarDisponibilidadDialog
        open={Boolean(disponibilidadRow)}
        empleado={disponibilidadRow ? { id: disponibilidadRow.id, nombre: nombreCompleto(disponibilidadRow), cargo: disponibilidadRow.cargo } : null}
        onClose={() => {
          setDisponibilidadRow(null);
          cargarListaHistorico();
          cargarLista();
        }}
      />

      <EmitirMemoDialog
        open={Boolean(memoRow)}
        empleado={memoRow ? { id: memoRow.id, nombre: nombreCompleto(memoRow) } : null}
        onClose={() => setMemoRow(null)}
        onEmitido={() => setMemoRow(null)}
      />

      <AdicionarPersonalDialog
        open={adicionarOpen}
        onClose={() => setAdicionarOpen(false)}
        onCreado={(nuevoId) => {
          setAdicionarOpen(false);
          navigate(`/administracion-personal/empleado/${nuevoId}`);
        }}
      />
    </DashboardLayout>
  );
};

export default AdministracionPersonalPage;
